import { ILanguageResource, NamedEnumLike } from './LanguageTypes'
import { PlaceInAkixiNameSpace } from '../utils/Utils'
import { StringUtils } from '../utils/StringUtils'
import { generateUpdateBundleFunction } from './LanguageResourcing'
import { AvailableLanguages } from '../../../i18n'
import { IsVarEmpty } from '../utils/GlobalFunctions'
import {
    TELEPHONY_SYSTEM_TYPE_CISCO_WEBEX_CALLING,
    TELEPHONY_SYSTEM_TYPE_MS_TEAMS,
    TelSysTypes
} from '../constants/TelephonyServerTypes'

type AkixiWindow = Window &
    typeof globalThis & {
        languageHandler?: CLanguageHandler
    }

interface ILanguageMap {
    [sLanguage: string]: ILanguageResource
}

interface IBundleMapping {
    [sLanguage: string]: (languageBundle: ILanguageResource) => void
}

export const getLanguageFromHTML = (): Nullable<string> =>
    (document.querySelector('input[data-akixi-language]') as HTMLInputElement)?.dataset?.akixiLanguage

const TYPE_TO_I18N_KEY: { [key in TelSysTypes | number]?: string } = {
    [TelSysTypes.MS_TEAMS]: 'MST',
    [TELEPHONY_SYSTEM_TYPE_MS_TEAMS]: 'MST',
    [TelSysTypes.WEBEX_CALLING]: 'WxC',
    [TELEPHONY_SYSTEM_TYPE_CISCO_WEBEX_CALLING]: 'WxC'
}

/**
 * A class that handles language resources.
 */
class CLanguageHandler {
    private m_sLanguageID: string
    private m_pLanguageMap: ILanguageMap = {}
    private m_pBundleMapping: IBundleMapping = {}
    private m_pLoadedBundles: string[] = []

    /**
     * @param {string} sLanguageID The language type to be using when refering to the language resources.
     */
    private constructor(sLanguageID = 'en_GB') {
        this.m_sLanguageID = sLanguageID
    }

    /**
     * Public method.
     * Deletes all attached resources.
     */
    public DeleteResources(): void {
        this.m_pLanguageMap = {}
        this.m_pBundleMapping = {}
        this.m_pLoadedBundles = []
    }

    /**
     * A public static method.
     *
     * Get the already initialised language instance.
     */
    public static getInstance(): CLanguageHandler {
        const $win: AkixiWindow = window as AkixiWindow

        if (IsVarEmpty($win.languageHandler)) {
            $win.languageHandler = new CLanguageHandler()
        }

        return $win.languageHandler
    }

    public getLanguageID(): string {
        return this.m_sLanguageID
    }

    /**
     * A public method.
     *
     * Get the current Language Map.
     *
     * @returns {ILanguageResource} - The current language map.
     *
     */
    public getLanguageMap(): ILanguageResource {
        return this.m_pLanguageMap[this.m_sLanguageID]
    }

    /**
     * A public method.
     *
     * A function that sets the language resource.
     *
     * @param {string} sLanguageID The StringLocale definition from the backend (e.g. "en_GB").
     */
    public setLanguage(sLanguageID: string): void {
        this.m_sLanguageID = sLanguageID
    }

    /**
     * A public method.
     *
     * A function that retrieves the language resource from the relevant enum.
     *
     * @param {string} sStringID The enum definition for the specific language resource.
     */
    public get(sStringID: string | number): string {
        return this.m_pLanguageMap[this.m_sLanguageID][sStringID]
    }

    /**
     * Get with a check to see if the "_MST" version of the key exists firsts.
     *
     * @param id - The enum definition for the specific language resource.
     * @param currentTelSysType - Current telephony system type.
     *
     * @returns {string} - The language resource.
     */
    public getWithTelSysCheck(id: string | number, currentTelSysType?: Nullable<number | string>): string {
        const telsysSpecificKey: Definable<string> = TYPE_TO_I18N_KEY[currentTelSysType as TelSysTypes | number]

        return this.get(`${id}_${telsysSpecificKey}`) ?? this.get(id)
    }

    /**
     * A function that retrieves the language resource from the relevant enum
     * and then formats that with a given set of parameters.
     *
     * @param id - The enum definition for the specific language resource.
     * @param replacements - List of strings to replace in order.
     */
    public getInterpolated(id: string | number, ...replacements: (Nullable<string> | Nullable<number>)[]): string {
        return StringUtils.interpolate(this.get(id), ...replacements)
    }

    /**
     * A public method.
     *
     * A function that registers a bundle of language resources to the main bundle object.
     *
     * @param {string} sBundleName The name of the bundle you're registering.
     * @param {Function} updateBundle A function to update the bundler.
     */
    public registerBundle(sBundleName: string, updateBundle: (languageBundle: ILanguageResource) => void): void {
        this.m_pBundleMapping[sBundleName] = updateBundle
    }

    /**
     * Register a number of bundles at once.
     *
     * @param enumeration - Enum of keys.
     * @param i18nJSONs - List of language files.
     *
     * @returns
     */
    public registerBundles<T extends Record<string, string>>(
        enumeration: NamedEnumLike<T>,
        i18nJSONs: AvailableLanguages['i18nBundles']
    ): void {
        Object.entries(i18nJSONs).forEach(([key, value]: [string, Record<string, string>]): void =>
            this.registerBundle(
                `${enumeration.NAME}_${key}`,
                generateUpdateBundleFunction(enumeration as NamedEnumLike<unknown>, value)
            )
        )
    }

    /**
     * A public method.
     *
     * A function that loads a bundle of language resources to then be accessed on the relevant pages.
     *
     * @param {string} sBundleName The name of the bundle you're loading.
     */
    public loadBundle(sBundleName: string): void {
        // Add the current language to the bundle name to create the real bundle name, e.g. General_en_GB.
        const sResolvedBundleName = `${sBundleName}_${this.m_sLanguageID}`
        if (this.m_pLoadedBundles.indexOf(sResolvedBundleName) === -1) {
            if (sResolvedBundleName in this.m_pBundleMapping) {
                // Retreive the update function.
                const pUpdateBundleFunction = this.m_pBundleMapping[sResolvedBundleName]
                // Add the language if not already present
                if (typeof this.m_pLanguageMap[this.m_sLanguageID] === 'undefined') {
                    this.m_pLanguageMap[this.m_sLanguageID] = {}
                }
                // Updates global hashmap.
                pUpdateBundleFunction(this.m_pLanguageMap[this.m_sLanguageID])
                // Update the list of loaded bundles.
                this.m_pLoadedBundles.push(sResolvedBundleName)
            } else {
                throw new Error(`Invalid language bundle: ${sResolvedBundleName}`)
            }
        }
    }

    /**
     * Loads multiple language bundles in a single call. See {@link loadBundle}.
     *
     * @param pBundleNames The list of bundle names to load.
     */
    public loadBundles(pBundleNames: readonly string[]): void {
        pBundleNames.forEach((sBundleName: string): void => this.loadBundle(sBundleName))
    }
}

PlaceInAkixiNameSpace('CLanguageHandler', CLanguageHandler)
export default CLanguageHandler
